import { useCase } from '@hooks/useCases';
import { useCaseWorkflow } from '@hooks/useCaseWorkflow';
import { useStore } from '@store/index';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorContainer } from '@/components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { formatCaseId } from '@/utils/cases';
import { getErrorButtonConfig } from '@/utils/errorButtonConfig';
import { buildRoutePath } from '@/utils/routes';

import { DefaultCaseDetailsPage } from './DefaultCaseDetailsPage';
import { getPageForWorkflow } from './registry';

interface CaseDetailsRouterProps {
  caseId: string;
  orgId?: string;
  progId?: string;
}

/**
 * CaseDetailsRouter - Dynamic component router for workflow-specific case details pages
 *
 * This component:
 * 1. Fetches the current workflow from the store
 * 2. Looks up a custom page component for that workflow type in the registry
 * 3. Renders the custom component if found, otherwise falls back to DefaultCaseDetailsPage
 *
 * This allows each workflow type to have a completely different case details page
 * with different layouts, components, and header actions.
 */
export const CaseDetailsRouter: React.FC<CaseDetailsRouterProps> = ({ caseId, orgId, progId }) => {
  const { setSelectedWorkflowId } = useStore();
  const navigate = useNavigate();

  // Fetch case details to get the workflow_type
  const { data: caseDetails, isLoading: caseLoading, error: caseError } = useCase(caseId);

  // Sync the case's workflow_id to the store so the breadcrumb shows the correct workflow.
  // Using workflow_id directly avoids ambiguity when multiple workflows share the same workflow_type.
  useEffect(() => {
    if (caseDetails?.workflow_id) {
      setSelectedWorkflowId(caseDetails.workflow_id);
    }
  }, [caseDetails?.workflow_id, setSelectedWorkflowId]);

  const selectedWorkflow = useCaseWorkflow(caseDetails?.workflow_id);

  // Look up custom page component for this workflow type
  const CustomPageComponent = useMemo(() => {
    const workflowType = selectedWorkflow?.workflow_type;
    if (!workflowType) {
      return null;
    }
    return getPageForWorkflow(workflowType);
  }, [selectedWorkflow?.workflow_type]);

  // If the case record API failed (e.g. 404), show error and block all downstream rendering
  if (!caseLoading && caseError && !caseDetails) {
    const errorButtonConfig = getErrorButtonConfig((caseError as any)?.APIHttpStatus, navigate);
    return (
      <>
        <BreadcrumbPortal>
          <Breadcrumb>
            <Breadcrumb.Item to={buildRoutePath('/cases', orgId || '', progId || '')}>
              Cases
            </Breadcrumb.Item>
            <Breadcrumb.Item>{formatCaseId(caseId)}</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbPortal>
        <div className="p-6 h-full">
          <ErrorContainer
            errorMessage={(caseError as any)?.userMessage}
            buttonText={errorButtonConfig?.text}
            buttonOnClick={errorButtonConfig?.onClick}
          />
        </div>
      </>
    );
  }

  // Show loading state while case record is being fetched or workflow is being determined.
  // This must block before rendering the detail page to prevent its hooks from firing.
  if (caseLoading || !selectedWorkflow) {
    return (
      <>
        <BreadcrumbPortal>
          <Breadcrumb>
            <Breadcrumb.Item to={buildRoutePath('/cases', orgId || '', progId || '')}>
              Cases
            </Breadcrumb.Item>
            <Breadcrumb.Item>Loading...</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbPortal>
        <div className="flex flex-col justify-center items-center h-full">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading workflow...</p>
        </div>
      </>
    );
  }

  // Use custom page if available, otherwise default
  const PageComponent = CustomPageComponent || DefaultCaseDetailsPage;

  return <PageComponent caseId={caseId} orgId={orgId} progId={progId} />;
};
